import React, { useState } from 'react';

import { currentWinner, initialSets } from './scoring-match-draft';
import { matchesOfPlayer } from './scoring-match-query';
import ScoringMatchSides from './scoring-match-sides';
import ScoringPlayerMatchesModal from './scoring-player-matches-modal';
import ScoringStandings from './scoring-standings';
import {
  groupKey,
  matchesOf,
  groupFormatLabel,
  usesPointSystem,
  type MatchItem,
  type ScoringGroup,
  type StandingRow,
} from './scoring-types';

interface Props {
  groups: ScoringGroup[];
  matches: MatchItem[];
  live: boolean;
}

interface PickedPlayer {
  group: ScoringGroup;
  row: StandingRow;
}

export const RoundScoringPanel: React.FC<Props> = ({ groups, matches, live }) => {
  const [picked, setPicked] = useState<PickedPlayer | null>(null);
  const playedGroups = groups.filter((group) => group.matchCount > 0);

  if (playedGroups.length === 0) {
    return null;
  }

  return (
    <section className="round-scoring" data-testid="round-scoring-panel">
      <h2 className="round-scoring__title">Tabela i mecze kolejki</h2>
      <p className="scoring-hint">
        {live
          ? 'Na żywo z wpisanych wyników. „Klas." to punkty do klasyfikacji sezonu za miejsce w grupie, a przy meczu widać szacunkową zmianę rankingu.'
          : '„Klas." to punkty do klasyfikacji sezonu za miejsce w grupie, a przy meczu widać zmianę rankingu.'}
      </p>
      <p className="scoring-hint">
        Kliknij zawodnika w tabeli — zobaczysz jego ranking i wszystkie mecze w tej grupie.
      </p>

      {playedGroups.map((group) => (
        <div
          className="round-scoring__group"
          key={groupKey(group)}
          data-testid={`round-scoring-group-${groupKey(group)}`}
        >
          <header className="round-scoring__group-head">
            <h3 className="round-scoring__group-name">
              {group.name === null ? 'Mecze kolejki' : `Grupa ${group.name}`}
            </h3>
            <span className="round-scoring__group-meta">
              {group.playedCount}/{group.matchCount} meczów · {groupFormatLabel(group)}
            </span>
          </header>

          <ScoringStandings
            rows={group.standings}
            showPoints={usesPointSystem(group)}
            testId={`round-standings-${groupKey(group)}`}
            onPickPlayer={(row) => setPicked({ group, row })}
          />

          <ul className="scoring-matches" data-testid={`round-scoring-matches-${groupKey(group)}`}>
            {matchesOf(matches, group).map((match) => (
              <li key={match.id} className="scoring-match-card">
                <div className="scoring-match-header">
                  <ScoringMatchSides
                    match={match}
                    sets={initialSets(match)}
                    winner={currentWinner(match)}
                  />
                  <div className="scoring-match-meta">
                    {match.winnerSide === null ? (
                      <span className="scoring-match__pending">Brak wyniku</span>
                    ) : (
                      <span className="scoring-match-result">✓</span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {picked !== null && (
        <ScoringPlayerMatchesModal
          player={picked.row}
          matches={matchesOfPlayer(matchesOf(matches, picked.group), picked.row.leaguePlayerId)}
          showPoints={usesPointSystem(picked.group)}
          scoring={picked.group.scoring}
          onClose={() => setPicked(null)}
        />
      )}
    </section>
  );
};

export default RoundScoringPanel;
